import {
  Action,
  combineReducers,
  createFeatureSelector,
  createSelector,
} from '@ngrx/store';
import { RmdAgeInfo } from '../../account-info-header/models/RmdAgeInfo';
import * as fromRmdAgeInfo from '../../account-info-header/reducers/rmd-age-info.reducer';
import {
  CreateTypes,
  PageNameTypes,
  tableActions,
  transactionTypes,
} from '../utilities/Constants';
import * as fromCancelModal from './cancel-modal.reducer';
import * as fromConfirmationPage from './confirmation-page.reducers';
import * as fromCreateTypeInContext from './create-type-in-context.reducer';
import * as fromEAuthInfo from './eAuth-Info.reducer';
import * as fromEditingRestrictedModal from './editing-restricted.reducer';
import * as fromFilePickerModal from './file-picker-modal-reducer';
import * as fromPageScrollToState from './page-scroll-to.reducer';
import * as fromReviewPage from './review-page.reducer';
import * as fromServiceError from './service-error.reducer';
import * as fromStandingAuthTableState from './standing-authorizations-table-state.reducers';
import * as fromViewInContextState from './view-inContext.reducer';
import * as fromViewPage from './view-page.reducer';
import * as fromPreviousViewInContextState from './previous-create-type-in-context.reducers';

export const sharedStateFeatureKey = 'sharedState';

export interface SharedState {
  [fromViewInContextState.viewInContextKey]: PageNameTypes;
  [fromPageScrollToState.pageScrollToKey]: string;
  [fromCancelModal.cancelModalKey]: fromCancelModal.cancelModalState;
  [fromStandingAuthTableState.standingAuthTableStateKey]: fromStandingAuthTableState.State;
  [fromReviewPage.reviewPageKey]: fromReviewPage.State;
  [fromConfirmationPage.confirmationPageKey]: fromConfirmationPage.State;
  [fromViewPage.viewPageKey]: fromViewPage.State;
  [fromServiceError.serviceErrorKey]: fromServiceError.State;
  [fromEditingRestrictedModal.editingRestrictedKey]: fromEditingRestrictedModal.editingRestrictedModalState;
  [fromEAuthInfo.eAuthInfoKey]: fromEAuthInfo.State;
  [fromCreateTypeInContext.createTypeKey]: CreateTypes;
  [fromRmdAgeInfo.rmdAgeInfoKey]: RmdAgeInfo;
  [fromFilePickerModal.filePickerModalKey]: fromFilePickerModal.filePickerModalState;
  [fromPreviousViewInContextState.previousContextKey]: CreateTypes;
}

export function reducers(state: SharedState | undefined, action: Action) {
  return combineReducers({
    [fromViewInContextState.viewInContextKey]:
      fromViewInContextState.createRequestStepReducer,
    [fromPageScrollToState.pageScrollToKey]:
      fromPageScrollToState.pageScrollToStateReducer,
    [fromCancelModal.cancelModalKey]:
      fromCancelModal.cancelModalReducer,
    [fromStandingAuthTableState.standingAuthTableStateKey]:
      fromStandingAuthTableState.standingAuthTableStateReducer,
    [fromReviewPage.reviewPageKey]: fromReviewPage.reviewPageReducer,
    [fromConfirmationPage.confirmationPageKey]:
      fromConfirmationPage.confirmationPageReducer,
    [fromViewPage.viewPageKey]: fromViewPage.viewPageReducer,
    [fromServiceError.serviceErrorKey]:
      fromServiceError.serviceErrorReducer,
    [fromEditingRestrictedModal.editingRestrictedKey]: fromEditingRestrictedModal.editingRestrictedModalReducer,
    [fromEAuthInfo.eAuthInfoKey]: fromEAuthInfo.eAuthInfoReducer,
    [fromCreateTypeInContext.createTypeKey]: fromCreateTypeInContext.createTypeReducer,
    [fromRmdAgeInfo.rmdAgeInfoKey]: fromRmdAgeInfo.rmdDateAgeReducer,
    [fromFilePickerModal.filePickerModalKey]: fromFilePickerModal.filePickerModalReducer,
    [fromPreviousViewInContextState.previousContextKey]: fromPreviousViewInContextState.previousContextReducer
  })(state, action);
}

export const selectSharedState = createFeatureSelector<SharedState>(
  sharedStateFeatureKey
);

export const selectViewInContext = createSelector(
  selectSharedState,
  (state) => {
    return state.step;
  }
);

export const selectPageScrollTo = createSelector(selectSharedState, (state) => {
  return state.scrollTo;
});

export const selectCreateTypeInContext = createSelector(selectSharedState, (state) => {
  return state.createTypeInContext;
});

export const selectStandingAuthTableState = createSelector(
  selectSharedState,
  (state) => {
    return state.standingAuthTableState;
  }
);

export const selectStandingAuthTablePrint = createSelector(
  selectStandingAuthTableState,
  (state) => {
    return state.printClicked;
  }
);

export const selectStandingAuthTableActionLinkState = createSelector(
  selectStandingAuthTableState,
  (state) => {
    return state.actionsLinkInfo;
  }
);

export const selectNicknameCallSucceeded = createSelector(
  selectStandingAuthTableState,
  (state) => {
    return state.addUpdateDeleteNickname?.nicknameCallSucceeded;
  }
);

export const selectStandingAuthTableEditTransactionClicked = createSelector(
  selectStandingAuthTableActionLinkState,
  (state) => {
    return (
      state.actionName === tableActions.edit && state.actionClicked
    );
  }
);

export const selectStandingAuthTableEditTransactionClickedACH = createSelector(
  selectStandingAuthTableEditTransactionClicked,
  selectStandingAuthTableActionLinkState,
  (editTransactionClicked, actionsLinkInfo) => {
    return (
      editTransactionClicked &&
      actionsLinkInfo.selectedRowData.transactionType === transactionTypes.ach
    );
  }
);

export const selectStandingAuthTableStartTransactionClicked = createSelector(
  selectStandingAuthTableActionLinkState,
  (state) => {
    return (
      state.actionName === tableActions.startTransaction && state.actionClicked
    );
  }
);

export const selectStandingAuthTableStartTransactionClickedACH = createSelector(
  selectStandingAuthTableStartTransactionClicked,
  selectStandingAuthTableActionLinkState,
  (startTransactionClicked, actionsLinkInfo) => {
    return (
      startTransactionClicked &&
      actionsLinkInfo.selectedRowData.transactionType === transactionTypes.ach
    );
  }
);

export const selectStandingAuthTableStartTransactionClickedWire =
  createSelector(
    selectStandingAuthTableStartTransactionClicked,
    selectStandingAuthTableActionLinkState,
    (startTransactionClicked, actionsLinkInfo) => {
      return (
        startTransactionClicked &&
        actionsLinkInfo.selectedRowData.transactionType ===
        transactionTypes.wire
      );
    }
  );

export const selectStandingAuthTableStartTransactionClickedInternalTransfer =
  createSelector(
    selectStandingAuthTableStartTransactionClicked,
    selectStandingAuthTableActionLinkState,
    (startTransactionClicked, actionsLinkInfo) => {
      return (
        startTransactionClicked &&
        actionsLinkInfo.selectedRowData.transactionType ===
        transactionTypes.internalTransfer
      );
    }
  );
export const selectStandingAuthTableStartTransactionClickedCheck =
  createSelector(
    selectStandingAuthTableStartTransactionClicked,
    selectStandingAuthTableActionLinkState,
    (startTransactionClicked, actionsLinkInfo) => {
      return (
        startTransactionClicked &&
        actionsLinkInfo.selectedRowData.transactionType ===
        transactionTypes.check
      );
    }
  );

  export const selectStandingAuthTableCurrentTab = createSelector(
    selectStandingAuthTableState,
    (state) => {
      return state.currentTab;
    }
  );

export const selectReviewPage = createSelector(selectSharedState, (state) => {
  return state.reviewPage;
});

export const selectAcknowledgement = createSelector(
  selectReviewPage,
  (state) => {
    return state.acknowledgement;
  }
);

export const selectAcknowledgementError = createSelector(
  selectAcknowledgement,
  (state) => {
    return state.error;
  }
);

export const selectAcknowledgementDateTime = createSelector(
  selectAcknowledgement,
  (state) => {
    return state.acknowledgementConsentTime;
  }
);

export const selectReviewPageSubmitClicked = createSelector(
  selectReviewPage,
  (state) => {
    return state.submitClicked;
  }
);

export const selectConfirmationPage = createSelector(
  selectSharedState,
  (state) => {
    return state.confirmationPage;
  }
);

export const selectCaseId = createSelector(selectConfirmationPage, (state) => {
  let caseId = state.caseId;
  if (state.submitCreateCaseId) {
    caseId = `${state.caseId}, ${state.submitCreateCaseId}`;
  }
  return caseId;
});

export const selectIsCreatePdfFlow = createSelector(selectConfirmationPage, (state) => {
  return state.isCreatePdfFlow;
});

export const selectTransactionStatus = createSelector(
  selectConfirmationPage,
  (state) => {
    return state.status;
  }
);

export const selectRequestedDate = createSelector(
  selectConfirmationPage,
  (state) => {
    return state.requestedDate;
  }
);

export const selectRequestedTime = createSelector(
  selectConfirmationPage,
  (state) => {
    return state.requestedTime;
  }
);

export const selectSource = createSelector(
  selectConfirmationPage,
  (state) => {
    return state.source;
  }
);

export const selectLoginId = createSelector(
  selectConfirmationPage,
  (state) => {
    return state.loginId;
  }
);

export const selectInitiatedBy = createSelector(
  selectConfirmationPage,
  (state) => {
    return state.initiatedBy;
  }
);

export const selectViewPage = createSelector(selectSharedState, (state) => {
  return state.viewPage;
});

export const selectViewPageServiceError = createSelector(
  selectViewPage,
  (state) => {
    return state.serviceError;
  }
);

export const selectCancelModal = createSelector(
  selectSharedState,
  (state) => {
    return state.cancelModal;
  }
);

export const selectCancelModalNextUrl = createSelector(
  selectCancelModal,
  (state) => {
    return state.nextUrlState;
  }
);

export const selectCancelModalActive = createSelector(
  selectCancelModal,
  (state) => {
    return state.active;
  }
);

export const selectCancelModalContinueClicked = createSelector(
  selectCancelModal,
  (state) => {
    return state.continueClicked;
  }
);

export const selectCancelModalContinueClickedTransactionReviewPage = createSelector(
  selectCancelModal,
  (state) => {
    return state.continueClicked && state.source === 'transactionReviewPage';
  }
);

export const selectCancelModalContinueClickedTransactionsTablePage = createSelector(
  selectCancelModal,
  (state) => {
    return state.continueClicked &&
      (state.source === 'transactionTableEdit' || state.source === 'transactionTableViewStatus' ||
        state.source === 'transactionTableStartTransaction' || state.source === 'transactionTableView')
  }
)

export const selectCancelModalContinueClickedForEditTwe = createSelector(
  selectCancelModal,
  (state) => {
    return state.continueClicked && (state.source === 'editTweModalInputPage' || state.source === 'editTweModalReviewPage');
  }
)

export const selectCancelModalContinueClickedForEditTweReviewPage = createSelector(
  selectCancelModal,
  (state) => {
    return state.continueClicked && state.source === 'editTweModalReviewPage';
  }
)

export const selectCancelModalSource = createSelector(selectCancelModal, (state) => state.source);

export const selectCancelModalTitle = createSelector(
  selectCancelModal,
  (state) => {
    return state.title;
  }
);

export const selectCancelModalContent = createSelector(
  selectCancelModal,
  (state) => {
    return state.content;
  }
);

export const selectCancelModalGreenButton = createSelector(
  selectCancelModal,
  (state) => {
    return state.greenButton;
  }
);

export const selectCancelModalGrayButton = createSelector(
  selectCancelModal,
  (state) => {
    return state.grayButton;
  }
);

export const selectCancelModalIsL1Navigation = createSelector(
  selectCancelModal,
  (state) => {
    return state.isL1Nav;
  }
);

export const selectCancelModalIsQuicklinksNavigation = createSelector(
  selectCancelModal,
  (state) => {
    return state.isQuicklinksNav;
  }
);


export const selectPreviousCreateTypeInContext = createSelector(
  selectSharedState,
  (state) => {
    return state.previousCreateTypeInContext;
  }
)

export const selectPreviousContextDifferentToCurrent = createSelector(
  selectPreviousCreateTypeInContext,
  selectCreateTypeInContext,
  (previousContext, currentContext) => {
    return previousContext.length > 0 && previousContext !== currentContext;
  }
)

export const selectFilePickerModal = createSelector(
  selectSharedState,
  (state) => {
    return state.filePickerModal;
  }
)

export const selectFilePickerModalActive = createSelector(
  selectFilePickerModal,
  (state) => {
    return state.active;
  }
)

export const selectFilePickerModalContent = createSelector(
  selectFilePickerModal,
  (state) => {
    return state.content;
  }
)

export const selectSharedServiceErrors = createSelector(
  selectSharedState,
  (state) => {
    return state.serviceErrors;
  }
);

export const selectEditingRestrictedModalState = createSelector(selectSharedState, (state) => {
  return state.editingRestrictedModal;
});

export const selectShowEditingRestrictedModal = createSelector(selectEditingRestrictedModalState, (state) => {
  return state.active;
});

export const selectEAuthInfo = createSelector(selectSharedState, (state) => {
  return state.eAuthInfo;
});

export const selectEAuthError = createSelector(selectEAuthInfo, (state) => {
  return state.eAuthError;
});

export const selectEAuthEmailAddressError = createSelector(selectEAuthInfo, (state) => {
  return state.eAuthEmailAddressError;
});

export const selectEAuthAccountHolderSelected = createSelector(selectEAuthInfo, (state) => {
  return state.isAccountHolderSelected;
});

export const selectEAuthIsOptedOut = createSelector(selectEAuthInfo, (state) => {
  return state.isOptedOut;
});

export const selectEAuthSelectedAccountHolderIds = createSelector(selectEAuthInfo, (state) => {
  return state.selectedAccountHolder;
});

export const selectIsCreateTransaction = createSelector(selectCreateTypeInContext, (state) => {
  return state === 'transaction';
});

export const selectIsCreateStandingAuth = createSelector(selectCreateTypeInContext, (state) => {
  return state === 'standing-authorization';
});

export const selectPdf = createSelector(selectReviewPage, (state) => {
  return state.pdf;
});

export const selectPdfs = createSelector(selectReviewPage, (state) => {
  return state.pdfs;
});

export const selectRmdAgeInfo = createSelector(selectSharedState, (state) => {
  return state.rmdAgeInfo;
});

export const selectAchAcknowledgeErrorList = createSelector(selectConfirmationPage, (state =>{
  return state.achErrorList;
}));

export const selectConfirmationPageTaxId = createSelector(selectConfirmationPage, (state => {
  return state.taxId;
}));

export const selectViewPageTaxId = createSelector(selectViewPage, (state => {
  return state.taxId;
}));

export const selectViewAccountDetails = createSelector(selectViewPage, (state => {
  return state.accountDetails;
}));
